import {useEffect, useMemo, useRef, useSyncExternalStore} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import type {RootState} from '../../../store/store';
import {CallStateEnum} from '../../../components/Dialer/callState';
import {
    getMatrixCallSessionSnapshot,
    MATRIX_CALL_BUSY_REASON,
    MATRIX_CALL_DECLINED_REASON,
    subscribeMatrixCallSession,
    type MatrixCallSessionSnapshot,
} from '../../../services/matrixCallService';
import {getSipControlsSnapshot, subscribeSipControls} from '../../../components/Dialer/services/sipService';
import {useGroupCallState, useIncomingGroupCall, useRoomCallSummary, useRtcFeatures} from '../../groupCalls/hooks/useGroupCall';
import {useMatrixContext} from '../../../providers/MatrixProvider';
import {useMatrixCallMedia} from '../matrix/useMatrixCallMedia';
import {
    groupLegOf,
    incomingGroupLegOf,
    matrixLegOf,
    phoneLegOf,
    type DialerCallSnapshot,
} from '../model/legs';
import type {CallLeg, CallSurfaceView, EndedCall} from '../model/types';
import {buildCallSurfaceView, endedCallOf, endedLegs} from '../model/view';
import {engineCallOf, setDialerHidden, type CallEngineTag} from '../../../store/features/calls/dialerSlice';
import {callEnded, callStarted, selectCallSurfaceState, selectDialerOpen} from '../store/callSurfaceSlice';

const IDLE_CALL = {callState: CallStateEnum.IDLE, incomingCall: false, callFrom: null, callTo: undefined, isSpam: '', ivrKeyName: '', inCallSince: undefined};

const selectEngineCall = (engine: CallEngineTag) => (state: RootState): DialerCallSnapshot => {
    const fields = engineCallOf(state.dialer, engine) ?? (state.dialer.callOwner ? IDLE_CALL : state.dialer);
    return {
        callState: fields.callState,
        incomingCall: fields.incomingCall,
        callFrom: fields.callFrom,
        callTo: fields.callTo ?? undefined,
        selectedPhoneNumber: state.dialer.selectedPhoneNumber,
        isSpam: fields.isSpam,
        ivrKeyName: fields.ivrKeyName,
        inCallSince: fields.inCallSince,
    };
};

const selectSipCall = selectEngineCall('sip');
const selectMatrixCallFrom = (state: RootState): unknown => selectEngineCall('matrix')(state).callFrom;

const useRoomName = (roomId: string | undefined): string | undefined => {
    const {rooms} = useMatrixContext();
    return roomId ? rooms.find(room => room.roomId === roomId)?.name || undefined : undefined;
};

export const useCallLegs = (): readonly CallLeg[] => {
    const sip = useSelector(selectSipCall, shallowEqual);
    const matrixCallFrom = useSelector(selectMatrixCallFrom);
    const matrix = useSyncExternalStore(subscribeMatrixCallSession, getMatrixCallSessionSnapshot);
    const sipControls = useSyncExternalStore(subscribeSipControls, getSipControlsSnapshot);
    const group = useGroupCallState();
    const ring = useIncomingGroupCall();
    const flags = useRtcFeatures();
    const groupRoomName = useRoomName(group.roomId);
    const sessionStartedAt = useRoomCallSummary(group.roomId)?.startedAt;
    const {muted, heldByMe, heldByPeer, dtmf} = useMatrixCallMedia(matrix.call);
    return useMemo(() => {
        const legs: CallLeg[] = [];
        const matrixLeg = matrixLegOf(matrix.state, matrixCallFrom, {muted, held: heldByMe, heldByPeer, dtmf}, flags);
        const phoneLeg = phoneLegOf(sip, sipControls);
        const groupLeg = groupLegOf(group, groupRoomName, flags, sessionStartedAt);
        [phoneLeg, matrixLeg, groupLeg].forEach(leg => leg && legs.push(leg));
        if (ring) {
            legs.push(incomingGroupLegOf(ring));
        }
        return legs;
    }, [sip, matrixCallFrom, matrix, sipControls, muted, heldByMe, heldByPeer, dtmf, group, ring, flags, groupRoomName, sessionStartedAt]);
};

export const useCallSurfaceView = (legs: readonly CallLeg[]): CallSurfaceView => {
    const state = useSelector(selectCallSurfaceState);
    const dialerOpen = useSelector(selectDialerOpen);
    return useMemo(() => buildCallSurfaceView(legs, {...state, dialerOpen}), [legs, state, dialerOpen]);
};

const REMOTE_OUTCOMES: Readonly<Record<string, EndedCall['remoteOutcome']>> = {
    [MATRIX_CALL_DECLINED_REASON]: 'declined',
    [MATRIX_CALL_BUSY_REASON]: 'busy',
};

const remoteOutcomeOf = (leg: CallLeg, matrix: MatrixCallSessionSnapshot): EndedCall['remoteOutcome'] =>
    leg.engine === 'matrix' && matrix.state.kind === 'ended' && leg.id === `matrix:${matrix.state.call.matrixCallId}`
        ? REMOTE_OUTCOMES[matrix.state.reason]
        : undefined;

export const useEndedCallTracker = (legs: readonly CallLeg[]): void => {
    const dispatch = useDispatch();
    const groupFailed = useGroupCallState().phase === 'failed';
    const previous = useRef<readonly CallLeg[]>([]);
    useEffect(() => {
        const ended = endedLegs(previous.current, legs);
        const started = legs.some(leg => !previous.current.some(before => before.id === leg.id));
        previous.current = legs;
        if (started) {
            dispatch(callStarted());
        }
        const last = ended[ended.length - 1];
        if (last && legs.length === 0 && !(last.engine === 'group' && groupFailed)) {
            dispatch(setDialerHidden(true));
            dispatch(callEnded({...endedCallOf(last, Date.now()), remoteOutcome: remoteOutcomeOf(last, getMatrixCallSessionSnapshot())}));
        }
    }, [dispatch, groupFailed, legs]);
};
